import React, { useEffect, useRef, useState } from "react";
import { FaceitLevel } from "./FaceitLevel";
import { useSnackbar } from "../contexts/SnackbarProvider";

interface Player {
    id: string;
    nickname: string;
    avatar?: string;
    games: Array<{
        name: string;
        skill_level: number;
    }>;
}

const requester = new TitarRequester();

export const WatchlistSearch = ({ onAdd }: { onAdd: (player: any) => void }) => {
    const { push } = useSnackbar();


    const rootRef = useRef<HTMLDivElement | null>(null);

    const [query, setQuery] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [players, setPlayers] = useState<Player[]>([]);
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<null | { id: string; nickname: string; avatar?: string }>(null);
    const controller = useRef<AbortController | null>(null);
    const typed = useRef(false);


    useEffect(() => {
        const onPointerDown = (e: PointerEvent) => {
            const root = rootRef.current;
            if (!root) return;

            if (!root.contains(e.target as Node)) {
                setOpen(false);
                typed.current = false;
            }
        };

        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setOpen(false);
                typed.current = false;
            }
        };

        document.addEventListener("pointerdown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.removeEventListener("pointerdown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, []);

    // поиск игроков
    useEffect(() => {
        if (query.length < 2) {
            setPlayers([]);
            setOpen(false);
            return;
        }

        if (controller.current) controller.current.abort();
        controller.current = new AbortController();

        const t = setTimeout(async () => {
            try {
                const res = await fetch(
                    `/watchlist/search/players?q=${encodeURIComponent(query)}`,
                    { signal: controller.current?.signal }
                );
                const json = await res.json();
                setPlayers(json.data || []);

                if (typed.current === true) {
                    setOpen(true);
                }
            } catch (e: any) {
                if (e.name !== "AbortError") console.error(e);
            }
        }, 250);

        return () => clearTimeout(t);
    }, [query]);

    const selectPlayer = (p: { id: string; nickname: string; avatar?: string }) => {
        if (isSubmitting) return;

        setSelected(p);
        typed.current = false;
        setQuery(p.nickname);
        setOpen(false);

        onSubmit(undefined, p.nickname);
    };

    const onSubmit = (e?: React.FormEvent, nickname?: string) => {
        if (e) {
            e.preventDefault();
        }

        if (isSubmitting) return;

        const dataform = new FormData();
        dataform.append("search", nickname ?? query);

        setIsSubmitting(true);
        setOpen(false);
        typed.current = false;

        requester.post("/watchlist/add", dataform).then((response: any) => {
            if (response.error) {
                throw new Error(response.error);
            }
            
            push({ variant: "success", message: "Player added to watchlist!" });
            onAdd(response);

            setQuery("");
            setSelected(null);
            setPlayers([]);
        }).catch((error: any) => {
            push({ variant: "error", message: error.message || "Failed to add player." });
        }).finally(() => {
            setIsSubmitting(false);
        });
    }

    return (
        <div ref={rootRef} className="rounded-md bg-primary p-2 flex">
            <form
                className="rounded-md bg-black p-2 gap-x-2 flex w-full"
                onSubmit={onSubmit}
            >
                <div className="relative w-full">
                    <input
                        className="rounded-md bg-primary-text placeholder:text-navbar-text px-4 py-2 min-w-[250px] w-full disabled:opacity-60"
                        placeholder="Enter nickname"
                        value={query}
                        disabled={isSubmitting}
                        onFocus={() => {
                            if (!isSubmitting && players.length > 0 && query.length >= 2) setOpen(true);
                        }}
                        onChange={(e) => {
                            if (isSubmitting) return;
                            typed.current = true;
                            setQuery(e.target.value);
                            setSelected(null);
                        }}
                        autoComplete="off"
                    />

                    {open && !isSubmitting && (
                        <div className="absolute z-50 mt-2 w-full rounded-md bg-black border border-white/10 shadow-lg overflow-hidden">
                            <ul className="max-h-72 overflow-auto">
                                {players.length === 0 && (
                                    <li className="px-4 py-3 text-sm text-white/60">
                                        No players found
                                    </li>
                                )}

                                {players.map((p) => {
                                    const game = p.games.find((g) => g.name === "cs2");

                                    return (
                                        <li
                                            className="flex justify-between items-center gap-3 px-4 py-2 cursor-pointer hover:bg-white/5"
                                            key={p.id}
                                            onClick={() => selectPlayer(p)}
                                        >
                                            <div className="flex gap-3 items-center">

                                                {p.avatar ? (
                                                    <img
                                                        src={p.avatar}
                                                        className="w-7 h-7 rounded-full object-cover"
                                                        alt=""
                                                    />
                                                ) : (
                                                    <div className="w-7 h-7 rounded-full bg-white/10" />
                                                )}

                                                <span className="text-white">{p.nickname}</span>
                                            </div>

                                            <div>
                                                <span className="w-6 h-6 flex"><FaceitLevel level={game?.skill_level ?? 3} /></span>
                                            </div>
                                        </li>

                                    )
                                })}
                            </ul>
                        </div>
                    )}
                </div>

                <button
                    disabled={isSubmitting}
                    className="rounded-md bg-pink hover:bg-pink-lighter disabled:opacity-50 transition-colors focus:bg-pink-dark text-white px-4 py-2 w-full max-w-[150px] flex items-center justify-center gap-2"
                >
                    {isSubmitting && (
                        <span
                            className="inline-block h-4 w-4 rounded-full border-2 border-white/40 border-t-white animate-spin"
                            aria-hidden="true"
                        />
                    )}
                    <span>{isSubmitting ? "Adding..." : "Add player"}</span>
                </button>
            </form>
        </div>
    );
}